import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Folder,
  File,
  FileText,
  Image,
  Video,
  Upload,
  FolderPlus,
  FilePlus,
  Trash2,
  Edit3,
  Download,
  ChevronRight,
  Home,
  X,
  Save,
  MoreVertical,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Axios already configured with interceptors in AuthContext

const getFileIcon = (file) => {
  if (file.is_folder) return Folder;
  const ext = file.filename.split(".").pop().toLowerCase();
  if (["txt", "md", "json", "xml", "csv"].includes(ext)) return FileText;
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) return Image;
  if (["mp4", "webm"].includes(ext)) return Video;
  return File;
};

export const FileManager = ({ section, allowedExtensions, canCreateText = false }) => {
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [showNewFile, setShowNewFile] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFileName, setNewFileName] = useState("");
  const [editorContent, setEditorContent] = useState("");
  const [renameValue, setRenameValue] = useState("");
  const [uploading, setUploading] = useState(false);

  const fetchFiles = useCallback(async () => {
    if (!navigator.onLine) {
      toast.error("You are offline");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/files/${section}`, {
        params: { path: currentPath },
      });
      setFiles(response.data.files || []);
    } catch (error) {
      toast.error("Failed to load files");
    } finally {
      setLoading(false);
    }
  }, [section, currentPath]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const navigateTo = (path) => {
    setCurrentPath(path);
    setSelectedFile(null);
  };

  const handleFolderClick = (folder) => {
    const newPath = currentPath ? `${currentPath}/${folder.filename}` : folder.filename;
    navigateTo(newPath);
  };

  const handleFileClick = async (file) => {
    if (file.is_folder) {
      // If folder is already selected, navigate into it
      if (selectedFile?.id === file.id) {
        handleFolderClick(file);
      } else {
        // First click selects the folder (allows delete/rename)
        setSelectedFile(file);
      }
    } else {
      // For all files: first click selects, second click opens
      if (selectedFile?.id === file.id) {
        // Second click - open the file
        if (file.mime_type?.startsWith("text/") || file.mime_type === "application/json") {
          await handleOpenEditor(file);
        } else if (file.mime_type?.startsWith("image/") || file.mime_type?.startsWith("video/")) {
          handlePreview(file);
        }
      } else {
        // First click - just select (shows action bar for delete/rename)
        setSelectedFile(file);
      }
    }
  };

  // Double click to open file/folder directly
  const handleFileDoubleClick = async (file) => {
    if (file.is_folder) {
      handleFolderClick(file);
    } else if (file.mime_type?.startsWith("text/") || file.mime_type === "application/json") {
      await handleOpenEditor(file);
    } else if (file.mime_type?.startsWith("image/") || file.mime_type?.startsWith("video/")) {
      handlePreview(file);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!navigator.onLine) {
      toast.error("You are offline. Cannot upload.");
      return;
    }

    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!allowedExtensions.includes(ext)) {
      toast.error(`File type not allowed. Allowed: ${allowedExtensions.join(", ")}`);
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("path", currentPath);

    try {
      await axios.post(`${API}/files/${section}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded");
      fetchFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setShowUpload(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    if (!navigator.onLine) {
      toast.error("You are offline");
      return;
    }

    try {
      await axios.post(`${API}/files/${section}/folder`, {
        name: newFolderName,
        parent_path: currentPath,
      });
      toast.success("Folder created");
      setNewFolderName("");
      setShowNewFolder(false);
      fetchFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create folder");
    }
  };

  const handleCreateTextFile = async () => {
    if (!newFileName.trim()) return;

    if (!navigator.onLine) {
      toast.error("You are offline");
      return;
    }

    try {
      await axios.post(
        `${API}/files/${section}/create-text`,
        { content: "" },
        { params: { filename: newFileName, path: currentPath } }
      );
      toast.success("File created");
      setNewFileName("");
      setShowNewFile(false);
      fetchFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create file");
    }
  };

  const handleOpenEditor = async (file) => {
    if (!navigator.onLine) {
      toast.error("You are offline");
      return;
    }

    try {
      const response = await axios.get(`${API}/files/${section}/content/${file.id}`);
      setEditorContent(response.data.content);
      setSelectedFile(file);
      setShowEditor(true);
    } catch (error) {
      toast.error("Cannot read file content");
    }
  };

  const handleSaveContent = async () => {
    if (!navigator.onLine) {
      toast.error("You are offline");
      return;
    }

    try {
      await axios.put(`${API}/files/${section}/content/${selectedFile.id}`, {
        content: editorContent,
      });
      toast.success("File saved");
      setShowEditor(false);
      fetchFiles();
    } catch (error) {
      toast.error("Failed to save file");
    }
  };

  const handleRename = async () => {
    if (!renameValue.trim()) return;

    if (!navigator.onLine) {
      toast.error("You are offline");
      return;
    }

    try {
      await axios.put(`${API}/files/${section}/rename/${selectedFile.id}`, {
        new_name: renameValue,
      });
      toast.success("Renamed successfully");
      setShowRename(false);
      setSelectedFile(null);
      fetchFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Rename failed");
    }
  };

  const handleDelete = async () => {
    if (!navigator.onLine) {
      toast.error("You are offline");
      return;
    }

    try {
      await axios.delete(`${API}/files/${section}/${selectedFile.id}`);
      toast.success("Deleted successfully");
      setShowDelete(false);
      setSelectedFile(null);
      fetchFiles();
    } catch (error) {
      toast.error("Delete failed");
    }
  };

  const handleDownload = async (file) => {
    if (!navigator.onLine) {
      toast.error("You are offline");
      return;
    }

    try {
      const response = await axios.get(`${API}/files/${section}/download/${file.id}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Download failed");
    }
  };

  const handlePreview = (file) => {
    setSelectedFile(file);
    setShowPreview(true);
  };

  // Breadcrumb
  const pathParts = currentPath ? currentPath.split("/") : [];

  return (
    <div className="p-4" data-testid={`file-manager-${section}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          onClick={() => setShowNewFolder(true)}
          className="flex items-center gap-2 px-3 py-2 border border-[#15803d] text-[#22c55e] hover:bg-[#22c55e] hover:text-[#050505] transition-colors text-sm"
          data-testid="new-folder-btn"
        >
          <FolderPlus size={16} />
          <span className="hidden sm:inline">New Folder</span>
        </button>

        {canCreateText && (
          <button
            onClick={() => setShowNewFile(true)}
            className="flex items-center gap-2 px-3 py-2 border border-[#15803d] text-[#22c55e] hover:bg-[#22c55e] hover:text-[#050505] transition-colors text-sm"
            data-testid="new-file-btn"
          >
            <FilePlus size={16} />
            <span className="hidden sm:inline">New File</span>
          </button>
        )}

        <label className="flex items-center gap-2 px-3 py-2 border border-[#06b6d4] text-[#06b6d4] hover:bg-[#06b6d4] hover:text-[#050505] transition-colors text-sm cursor-pointer">
          <Upload size={16} />
          <span className="hidden sm:inline">{uploading ? "Uploading..." : "Upload"}</span>
          <input
            type="file"
            className="hidden"
            onChange={handleUpload}
            accept={allowedExtensions.join(",")}
            disabled={uploading}
            data-testid="upload-input"
          />
        </label>
      </div>

      {/* Breadcrumb */}
      <div className="breadcrumb mb-4 p-2 bg-[#0a0a0a] border border-[#15803d]">
        <button
          onClick={() => navigateTo("")}
          className="breadcrumb-item flex items-center gap-1"
        >
          <Home size={14} />
          <span>root</span>
        </button>
        {pathParts.map((part, index) => (
          <span key={index} className="flex items-center gap-1">
            <ChevronRight size={14} className="text-[#15803d]" />
            <button
              onClick={() => navigateTo(pathParts.slice(0, index + 1).join("/"))}
              className="breadcrumb-item"
            >
              {part}
            </button>
          </span>
        ))}
      </div>

      {/* File Grid */}
      {loading ? (
        <div className="text-[#22c55e] text-center py-8">[ LOADING... ]</div>
      ) : files.length === 0 ? (
        <div className="text-[#4ade80] text-center py-8 opacity-60">
          [ EMPTY DIRECTORY ]
        </div>
      ) : (
        <div className="file-grid">
          {files.map((file) => {
            const Icon = getFileIcon(file);
            return (
              <div
                key={file.id}
                className={`file-item ${selectedFile?.id === file.id ? "selected" : ""}`}
                onClick={() => handleFileClick(file)}
                onDoubleClick={() => handleFileDoubleClick(file)}
                data-testid={`file-item-${file.filename}`}
              >
                <Icon
                  size={40}
                  className={file.is_folder ? "text-[#f59e0b]" : "text-[#22c55e]"}
                />
                <span className="text-[#4ade80] text-xs text-center mt-2 break-all line-clamp-2">
                  {file.filename}
                </span>

                {/* Context menu */}
                {!file.is_folder && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="absolute top-1 right-1 p-1 text-[#4ade80] hover:text-[#22c55e]"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreVertical size={16} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-[#0a0a0a] border-[#15803d]">
                      {file.mime_type?.startsWith("text/") && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditor(file);
                          }}
                          className="text-[#22c55e] focus:bg-[#15803d] focus:text-[#22c55e]"
                        >
                          <Edit3 size={14} className="mr-2" />
                          Edit
                        </DropdownMenuItem>
                      )}
                      {(file.mime_type?.startsWith("image/") ||
                        file.mime_type?.startsWith("video/")) && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePreview(file);
                          }}
                          className="text-[#22c55e] focus:bg-[#15803d] focus:text-[#22c55e]"
                        >
                          <Image size={14} className="mr-2" />
                          Preview
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(file);
                        }}
                        className="text-[#06b6d4] focus:bg-[#15803d] focus:text-[#06b6d4]"
                      >
                        <Download size={14} className="mr-2" />
                        Download
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(file);
                          setRenameValue(file.filename);
                          setShowRename(true);
                        }}
                        className="text-[#f59e0b] focus:bg-[#15803d] focus:text-[#f59e0b]"
                      >
                        <Edit3 size={14} className="mr-2" />
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(file);
                          setShowDelete(true);
                        }}
                        className="text-[#ef4444] focus:bg-[#ef4444]/10 focus:text-[#ef4444]"
                      >
                        <Trash2 size={14} className="mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* Folder context menu */}
                {file.is_folder && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="absolute top-1 right-1 p-1 text-[#4ade80] hover:text-[#22c55e]"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreVertical size={16} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-[#0a0a0a] border-[#15803d]">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(file);
                          setRenameValue(file.filename);
                          setShowRename(true);
                        }}
                        className="text-[#f59e0b] focus:bg-[#15803d] focus:text-[#f59e0b]"
                      >
                        <Edit3 size={14} className="mr-2" />
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(file);
                          setShowDelete(true);
                        }}
                        className="text-[#ef4444] focus:bg-[#ef4444]/10 focus:text-[#ef4444]"
                      >
                        <Trash2 size={14} className="mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Action Bar - Shows when file/folder selected (both mobile and desktop) */}
      {selectedFile && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#0a0a0a] border-t-2 border-[#15803d] p-3 flex items-center justify-center gap-4 z-50">
          <div className="text-[#4ade80] text-sm truncate px-2 max-w-[200px] border-r border-[#15803d] pr-4">
            <span className="text-[#15803d]">Selected:</span> {selectedFile.filename}
          </div>
          
          {/* Open folder */}
          {selectedFile.is_folder && (
            <button
              onClick={() => handleFolderClick(selectedFile)}
              className="flex items-center gap-2 px-4 py-2 border border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e] hover:text-[#050505] transition-colors"
            >
              <Folder size={18} />
              <span className="hidden sm:inline">Open</span>
            </button>
          )}

          {/* View/Edit for text files */}
          {!selectedFile.is_folder && selectedFile.mime_type?.startsWith("text/") && (
            <button
              onClick={() => handleOpenEditor(selectedFile)}
              className="flex items-center gap-2 px-4 py-2 border border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e] hover:text-[#050505] transition-colors"
            >
              <Edit3 size={18} />
              <span className="hidden sm:inline">Edit</span>
            </button>
          )}

          {/* Preview for media */}
          {!selectedFile.is_folder && (selectedFile.mime_type?.startsWith("image/") || selectedFile.mime_type?.startsWith("video/")) && (
            <button
              onClick={() => handlePreview(selectedFile)}
              className="flex items-center gap-2 px-4 py-2 border border-[#22c55e] text-[#22c55e] hover:bg-[#22c55e] hover:text-[#050505] transition-colors"
            >
              <Image size={18} />
              <span className="hidden sm:inline">View</span>
            </button>
          )}

          {/* Download for files */}
          {!selectedFile.is_folder && (
            <button
              onClick={() => handleDownload(selectedFile)}
              className="flex items-center gap-2 px-4 py-2 border border-[#06b6d4] text-[#06b6d4] hover:bg-[#06b6d4] hover:text-[#050505] transition-colors"
            >
              <Download size={18} />
              <span className="hidden sm:inline">Download</span>
            </button>
          )}

          {/* Rename */}
          <button
            onClick={() => {
              setRenameValue(selectedFile.filename);
              setShowRename(true);
            }}
            className="flex items-center gap-2 px-4 py-2 border border-[#f59e0b] text-[#f59e0b] hover:bg-[#f59e0b] hover:text-[#050505] transition-colors"
          >
            <Edit3 size={18} />
            <span className="hidden sm:inline">Rename</span>
          </button>

          {/* Delete */}
          <button
            onClick={() => setShowDelete(true)}
            className="flex items-center gap-2 px-4 py-2 border border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white transition-colors"
          >
            <Trash2 size={18} />
            <span className="hidden sm:inline">Delete</span>
          </button>

          {/* Close selection */}
          <button
            onClick={() => setSelectedFile(null)}
            className="flex items-center gap-2 px-4 py-2 border border-[#4ade80] text-[#4ade80] hover:bg-[#4ade80] hover:text-[#050505] transition-colors"
          >
            <X size={18} />
            <span className="hidden sm:inline">Close</span>
          </button>
        </div>
      )}

      {/* New Folder Dialog */}
      <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
        <DialogContent className="dialog-content">
          <DialogHeader>
            <DialogTitle className="text-[#22c55e] font-['VT323'] text-xl">
              [ NEW FOLDER ]
            </DialogTitle>
          </DialogHeader>
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="Folder name..."
            className="w-full bg-transparent border border-[#15803d] text-[#22c55e] px-4 py-3 focus:border-[#22c55e] focus:outline-none font-mono"
            data-testid="new-folder-input"
          />
          <DialogFooter>
            <button
              onClick={() => setShowNewFolder(false)}
              className="px-4 py-2 border border-[#15803d] text-[#4ade80]"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateFolder}
              className="px-4 py-2 bg-[#22c55e] text-[#050505]"
              data-testid="create-folder-btn"
            >
              Create
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Text File Dialog */}
      <Dialog open={showNewFile} onOpenChange={setShowNewFile}>
        <DialogContent className="dialog-content">
          <DialogHeader>
            <DialogTitle className="text-[#22c55e] font-['VT323'] text-xl">
              [ NEW TEXT FILE ]
            </DialogTitle>
          </DialogHeader>
          <input
            type="text"
            value={newFileName}
            onChange={(e) => setNewFileName(e.target.value)}
            placeholder="filename.txt"
            className="w-full bg-transparent border border-[#15803d] text-[#22c55e] px-4 py-3 focus:border-[#22c55e] focus:outline-none font-mono"
            data-testid="new-file-input"
          />
          <DialogFooter>
            <button
              onClick={() => setShowNewFile(false)}
              className="px-4 py-2 border border-[#15803d] text-[#4ade80]"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateTextFile}
              className="px-4 py-2 bg-[#22c55e] text-[#050505]"
              data-testid="create-file-btn"
            >
              Create
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Text Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="dialog-content max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-[#22c55e] font-['VT323'] text-xl flex items-center gap-2">
              <Edit3 size={20} />
              {selectedFile?.filename}
            </DialogTitle>
          </DialogHeader>
          <textarea
            value={editorContent}
            onChange={(e) => setEditorContent(e.target.value)}
            className="w-full h-64 bg-[#050505] border border-[#15803d] text-[#22c55e] p-4 font-mono text-sm resize-none focus:outline-none focus:border-[#22c55e]"
            data-testid="file-editor"
          />
          <DialogFooter>
            <button
              onClick={() => setShowEditor(false)}
              className="px-4 py-2 border border-[#15803d] text-[#4ade80]"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveContent}
              className="px-4 py-2 bg-[#22c55e] text-[#050505] flex items-center gap-2"
              data-testid="save-file-btn"
            >
              <Save size={16} />
              Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={showRename} onOpenChange={setShowRename}>
        <DialogContent className="dialog-content">
          <DialogHeader>
            <DialogTitle className="text-[#22c55e] font-['VT323'] text-xl">
              [ RENAME ]
            </DialogTitle>
          </DialogHeader>
          <input
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            className="w-full bg-transparent border border-[#15803d] text-[#22c55e] px-4 py-3 focus:border-[#22c55e] focus:outline-none font-mono"
            data-testid="rename-input"
          />
          <DialogFooter>
            <button
              onClick={() => setShowRename(false)}
              className="px-4 py-2 border border-[#15803d] text-[#4ade80]"
            >
              Cancel
            </button>
            <button
              onClick={handleRename}
              className="px-4 py-2 bg-[#22c55e] text-[#050505]"
              data-testid="confirm-rename-btn"
            >
              Rename
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent className="dialog-content">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#ef4444] font-['VT323'] text-xl">
              [ CONFIRM DELETE ]
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#4ade80]">
              Are you sure you want to delete "{selectedFile?.filename}"?
              {selectedFile?.is_folder && " This will delete all contents inside."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border border-[#15803d] text-[#4ade80] bg-transparent hover:bg-[#15803d]/20">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-[#ef4444] text-white hover:bg-[#dc2626]"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="dialog-content max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-[#22c55e] font-['VT323'] text-xl flex items-center justify-between">
              <span>{selectedFile?.filename}</span>
              <button onClick={() => setShowPreview(false)}>
                <X size={20} />
              </button>
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4 bg-[#050505]">
            {selectedFile?.mime_type?.startsWith("image/") && (
              <img
                src={`${API}/files/${section}/serve/${selectedFile.id}`}
                alt={selectedFile.filename}
                className="max-w-full max-h-[60vh] object-contain"
              />
            )}
            {selectedFile?.mime_type?.startsWith("video/") && (
              <video
                src={`${API}/files/${section}/serve/${selectedFile.id}`}
                controls
                className="max-w-full max-h-[60vh]"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FileManager;
